import React, { useState, useEffect, useCallback } from 'react';
import { LoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import { fetchMarkers, saveArea, deleteRating } from '../services/api';
import HOC from './HOC';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const cityCenters = {
  Breda: { lat: 51.5719, lng: 4.7683 },
  Amsterdam: { lat: 52.3676, lng: 4.9041 },
};

const mapStyles = [
  { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'on' }] },
];

const FullScreenGoogleEarth = () => {
  const [city] = useState('Breda');
  const [markers, setMarkers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    _id: '',
    name: '',
    color: '#ff0000',
    number: '',
    shape: 'circle',
    latitude: '',
    longitude: '',
  });
  const [editing, setEditing] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [mapLoadTimeout, setMapLoadTimeout] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [hasAutoRefreshed, setHasAutoRefreshed] = useState(false);
  const maxRetries = 3;

  const loadMarkers = useCallback(async () => {
    try {
      const data = await fetchMarkers();
      console.log('Fetched markers:', data);
      setMarkers(Array.isArray(data) ? data : data.Response || []);
      setError(null);
      setRetryCount(0);
    } catch (error) {
      console.error('Error fetching markers:', error);
      if (retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          setRetryCount(retryCount + 1);
          loadMarkers();
        }, delay);
      } else {
        setError('Unable to load ratings after several attempts. Please try again.');
      }
    }
  }, [retryCount]);

  useEffect(() => {
    const refreshed = sessionStorage.getItem('mapAutoRefreshed');
    if (refreshed) {
      setHasAutoRefreshed(true);
      sessionStorage.removeItem('mapAutoRefreshed');
    }

    if (scriptLoaded) {
      loadMarkers();
    }

    const timeout = setTimeout(() => {
      if (!scriptLoaded && !hasAutoRefreshed) {
        setMapLoadTimeout(true);
        setError('Unable to load Google Maps. Refreshing…');
        setTimeout(() => {
          sessionStorage.setItem('mapAutoRefreshed', 'true');
          window.location.reload();
        }, 200);
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [scriptLoaded, hasAutoRefreshed, loadMarkers]);

  const mapClick = (e) => {
    setForm({
      ...form,
      _id: '',
      latitude: e.latLng.lat().toString(),
      longitude: e.latLng.lng().toString(),
    });
    setEditing(false);
    setShowPopup(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted, editing:', editing, 'form._id:', form._id);
    if (!form.name.trim() || form.name.length < 3 || form.name.length > 50) {
      toast.error('Name must be between 3 and 50 characters');
      return;
    }
    if (!/^\d+(\.\d{1})?$/.test(form.number)) {
      toast.error('Number must have at most one decimal place (e.g., 1.5)');
      return;
    }
    try {
      const payload = {
        ...form,
        city,
        coordinates: [parseFloat(form.latitude), parseFloat(form.longitude)],
      };
      console.log("Dta", form);

      const res = await saveArea(payload);
      console.log('Save response:', res);
      if (res.success) {
        await loadMarkers();
        setForm({
          _id: '',
          name: '',
          color: '#ff0000',
          number: '',
          shape: 'circle',
          latitude: '',
          longitude: '',
        });
        setSelected(null);
        setShowPopup(false);
        toast.success(
          res.ResponseMessage
        );

      } else {
        toast.error('Failed to save rating: ' + res.ResponseMessage);
      }
    } catch (error) {
      console.error('Error saving area:', error);
      toast.error('Error saving rating: ' + error.message);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setForm({
      _id: '',
      name: '',
      color: '#ff0000',
      number: '',
      shape: 'circle',
      latitude: '',
      longitude: '',
    });
    setEditing(false);
    setSelected(null);
  };

  const handleRetry = () => {
    setError(null);
    setMapLoadTimeout(false);
    setRetryCount(0);
    setScriptLoaded(false);
    loadMarkers();
  };

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: 9999 }}
      />
      <div className="flex h-screen -mt-20">
        <style>
          {`
            input[type="number"].no-arrows::-webkit-inner-spin-button,
            input[type="number"].no-arrows::-webkit-outer-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type="number"].no-arrows {
              -moz-appearance: textfield;
              appearance: textfield;
            }
          `}
        </style>
        <LoadScript
          googleMapsApiKey="AIzaSyBdwa9nNF-lhPPGNRPzCiXcngQNkSVw34M"
          onLoad={() => setScriptLoaded(true)}
          onError={(err) => {
            console.error('LoadScript Error:', err);
            setError('Failed to load Google Maps. Please check your connection or API key.');
            setMapLoadTimeout(true);
          }}
        >
          {error ? (
            <div className="flex items-center justify-center h-full flex-col gap-4">
              <p className="text-red-500">{error}</p>
              <button
                onClick={handleRetry}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : !scriptLoaded ? (
            <div className="flex items-center justify-center h-full flex-col gap-4">
              <p>Loading Google Maps{retryCount > 0 ? ` (Attempt ${retryCount + 1}/${maxRetries + 1})` : '...'}</p>
              {mapLoadTimeout && (
                <button
                  onClick={handleRetry}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Retry
                </button>
              )}
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100vh' }}
              center={
                markers.length > 0
                  ? { lat: markers[0].coordinates[0], lng: markers[0].coordinates[1] }
                  : cityCenters[city]
              }
              zoom={14}
              onClick={mapClick}
              options={{
                streetViewControl: false,
                fullscreenControl: true,
                mapTypeControl: false,
                mapTypeId: 'roadmap',
                styles: mapStyles,
                restriction: {
                  latLngBounds: { north: 53.7, south: 50.5, west: 3.2, east: 7.2 },
                  strictBounds: true,
                },
              }}
            >
              {markers.map((m) => {
                const lat = m.coordinates[0];
                const lng = m.coordinates[1];
                let iconPath = null;
                let isSymbol = true;

                switch (m.shape) {
                  case 'circle':
                    iconPath = window.google.maps.SymbolPath.CIRCLE;
                    break;
                  case 'marker':
                  default:
                    isSymbol = false;
                    break;
                }

                const markerProps = {
                  key: m._id,
                  position: { lat, lng },
                  onClick: () => {
                    setSelected(m);
                    setEditing(true);
                    setShowPopup(true);
                    setForm({
                      _id: m._id,
                      name: m.name,
                      color: m.color,
                      number: m.number,
                      shape: m.shape,
                      latitude: lat.toString(),
                      longitude: lng.toString(),
                    });
                  },
                  label: {
                    text: String(m.number),
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '14px',
                  },
                };

                if (isSymbol) {
                  markerProps.icon = {
                    path: iconPath,
                    fillColor: m.color,
                    fillOpacity: 1,
                    strokeWeight: 1,
                    scale: 10,
                  };
                } else {
                  markerProps.icon = {
                    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
                    fillColor: m.color,
                    fillOpacity: 1,
                    strokeColor: '#000',
                    strokeWeight: 1,
                    scale: 1.5,
                    anchor: new window.google.maps.Point(12, 24),
                    labelOrigin: new window.google.maps.Point(12, 10),
                  };
                }

                return <Marker {...markerProps} />;
              })}
            </GoogleMap>
          )}
        </LoadScript>

        {showPopup && (
          <div
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 shadow-lg rounded-lg w-64"
            style={{ zIndex: 1000 }}
          >
            <button
              type="button"
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={closePopup}
              title="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-bold mb-2">{editing ? 'Update Rating' : 'Submit Rating'}</h2>
            <form >
              <label className="mb-1 flex justify-between text-sm font-medium text-slate-700">Name</label>
              <input
                required
                minLength={3}
                maxLength={50}
                className="w-full mb-2 p-1 border rounded"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <label className="flex justify-between mb-1">Choose Color:</label>
              <div className="relative">
                <input
                  type="color"
                  className="w-full mb-2 cursor-pointer"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                />
              </div>
              <label className="flex justify-between mb-1">Number:</label>
              <input
                required
                type="number"
                min="0"
                step="0.1"
                className="w-full mb-2 p-1 border rounded no-arrows"
                value={form.number}
                onChange={(e) => setForm({ ...form, number: e.target.value })}
              />
              <label className="flex justify-between mb-1">Shape:</label>
              <select
                className="w-full mb-2 p-1 border rounded"
                value={form.shape}
                onChange={(e) => setForm({ ...form, shape: e.target.value })}
              >
                <option value="circle">Circle</option>
                <option value="marker">Marker</option>
              </select>
              <input
                required
                type="number"
                hidden
                step="any"
                className="w-full mb-2 p-1 border rounded"
                value={form.latitude}
                onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              />
              <input
                required
                type="number"
                hidden
                step="any"
                放下
                className="w-full mb-2 p-1 border rounded"
                value={form.longitude}
                onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              />
              <div className="flex flex-col items-center justify-center mt-6 gap-3">
                <button
                  type="submit"
                  className="w-full min-w-[200px] rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 px-8 py-3 text-sm sm:text-base font-semibold text-white shadow-md transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2"
                  onClick={(e) => handleFormSubmit(e)}
                >
                  {editing ? 'Update Rating' : 'Submit Rating'}
                </button>
                <div className="flex justify-center gap-2 w-full">
                {editing && (
                  <button
                    type="button"
                    className="rounded-full bg-red-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowConfirmDelete(true);
                    }}
                  >
                    Delete
                  </button>
                )}
                </div>
                {showConfirmDelete && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
                    <div className="relative w-full max-w-sm animate-[bsi-modal-pop_220ms_ease-out] overflow-hidden rounded-[1.75rem] border border-sky-100 bg-gradient-to-br from-white via-white to-sky-50 p-6 text-center shadow-2xl shadow-slate-950/20 ring-1 ring-white/80">
                      <div className="pointer-events-none absolute -right-10 -top-12 h-28 w-28 rounded-full bg-sky-200/50 blur-3xl" />
                      <div className="relative mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 text-lg font-extrabold text-white shadow-lg shadow-red-500/25">
                        !
                      </div>
                      <h2 className="relative text-xl font-extrabold tracking-tight text-slate-950">Delete this rating?</h2>
                      <p className="relative mt-2 text-sm font-medium leading-6 text-slate-600">This action cannot be undone.</p>
                      <div className="relative mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
                        <button
                          type="button"
                          className="w-full rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-sky-100 sm:w-auto"
                          onClick={() => setShowConfirmDelete(false)}
                        >
                          Cancel
                        </button>
                        <button
                        type="button"
                          className="w-full rounded-2xl bg-gradient-to-r from-red-500 to-rose-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-red-500/20 transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-red-600/25 focus:outline-none focus:ring-4 focus:ring-red-100 sm:w-auto"
                          onClick={async (e) => {
                            try {
                              const res = await deleteRating({ id: form._id });
                              console.log('Delete response:', res);
                              if (res.success) {
                                console.log('Delete successful, showing toast');
                                await loadMarkers();
                                toast.success('Rating deleted successfully!');
                                setTimeout(() => {
                                  closePopup();
                                }, 500);
                              } else {
                                toast.error('Delete failed: ' + res.ResponseMessage);
                              }
                            } catch (error) {
                              console.error('Delete error:', error);
                              toast.error('An error occurred while deleting the rating.');
                            } finally {
                              e.stopPropagation();
                              setShowConfirmDelete(false);
                              setEditing(false);
                            }
                          }}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
};

export default HOC(FullScreenGoogleEarth);



// import React, { useState, useEffect, useCallback } from 'react';
// import { useParams, useLocation } from 'react-router-dom';
// import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import HOC from './HOC';
// import { getCityRedirect, fetchMarkers, saveArea, deleteRating } from '../services/api';
// import RedirectUrl from "../BaseUrl";

// const base = RedirectUrl();


// const mapStyles = [
//   { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
//   { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
//   { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] },
//   { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'on' }] },
// ];

// const SerendipityMapView = () => {
//   const { city } = useParams();
//   const location = useLocation();
//   const normalizedCity = city ? city.toLowerCase() : 'breda';
//   const [scriptLoaded, setScriptLoaded] = useState(false);
//   const [error, setError] = useState(null);
//   const [redirectAttempted, setRedirectAttempted] = useState(false);
//   const [markers, setMarkers] = useState([]);
//   const [selected, setSelected] = useState(null);
//   const [center, setCenter] = useState({ lat: 22.7196, lng: 75.8577 }); // Default to Indore
//   const [form, setForm] = useState({
//     _id: '',
//     name: '',
//     color: '#ff0000',
//     number: '',
//     shape: 'circle',
//     latitude: '',
//     longitude: '',
//   });
//   const [editing, setEditing] = useState(false);
//   const [showPopup, setShowPopup] = useState(false);
//   const [showConfirmDelete, setShowConfirmDelete] = useState(false);
//   const [mapLoadTimeout, setMapLoadTimeout] = useState(false);
//   const [retryCount, setRetryCount] = useState(0);
//   const [hasAutoRefreshed, setHasAutoRefreshed] = useState(false);
//   const maxRetries = 3;

//   const loadMarkers = useCallback(async () => {
//     try {
//       const data = await fetchMarkers();
//       console.log('Fetched markers:', data);
//       setMarkers(Array.isArray(data) ? data : data.Response || []);
//       setError(null);
//       setRetryCount(0);
//     } catch (error) {
//       console.error('Error fetching markers:', error);
//       if (retryCount < maxRetries) {
//         const delay = Math.pow(2, retryCount) * 1000;
//         setTimeout(() => {
//           setRetryCount(retryCount + 1);
//           loadMarkers();
//         }, delay);
//       } else {
//         setError('Unable to load ratings after several attempts. Please try again.');
//       }
//     }
//   }, [retryCount]);

//   useEffect(() => {
//     const triggerRedirect = async () => {
//       try {
//         const redirectData = await getCityRedirect(normalizedCity);
//         console.log('redirectData:', redirectData);

//         if (redirectData && redirectData.success && redirectData.Response) {
//           const { latitude, longitude, redirect_url } = redirectData.Response;

//           // Validate and set coordinates
//           if (typeof latitude === 'number' && typeof longitude === 'number') {
//             setCenter({ lat: latitude, lng: longitude });
//           } else {
//             console.warn('No valid coordinates found in API response for city:', normalizedCity);
//           }

//           // Handle redirect only if redirect_url differs from current URL
//           if (redirect_url && redirect_url !== window.location.href) {
//             console.log(`Redirecting to: ${redirect_url}`);
//             window.location.href = `${base+city}`;
//           } else {
//             console.warn(
//               redirect_url === window.location.href
//                 ? 'Redirect URL matches current URL, skipping redirect'
//                 : 'No valid redirect URL found for city:',
//               normalizedCity
//             );
//             setRedirectAttempted(true); // Allow map to load
//           }
//         } else {
//           console.warn('No valid redirect data found for city:', normalizedCity);
//           setRedirectAttempted(true); // Allow map to load
//         }
//       } catch (err) {
//         console.error('Redirect error:', err.message);
//         setError('Failed to fetch city data. Please try again.');
//         setRedirectAttempted(true); // Allow map to load
//       }
//     };

//     triggerRedirect();
//   }, [normalizedCity]);

//   useEffect(() => {
//     const refreshed = sessionStorage.getItem('mapAutoRefreshed');
//     if (refreshed) {
//       setHasAutoRefreshed(true);
//       sessionStorage.removeItem('mapAutoRefreshed');
//     }

//     if (scriptLoaded && redirectAttempted) {
//       loadMarkers();
//     }

//     const timeout = setTimeout(() => {
//       if (!scriptLoaded && !hasAutoRefreshed) {
//         setMapLoadTimeout(true);
//         setError('Unable to load Google Maps. Refreshing…');
//         setTimeout(() => {
//           sessionStorage.setItem('mapAutoRefreshed', 'true');
//           window.location.reload();
//         }, 200);
//       }
//     }, 5000);

//     return () => clearTimeout(timeout);
//   }, [scriptLoaded, hasAutoRefreshed, redirectAttempted, loadMarkers]);

//   const mapClick = (e) => {
//     setForm({
//       ...form,
//       _id: '',
//       latitude: e.latLng.lat().toString(),
//       longitude: e.latLng.lng().toString(),
//     });
//     setEditing(false);
//     setShowPopup(true);
//   };

//   const handleFormSubmit = async (e) => {
//     e.preventDefault();
//     if (!form.name.trim() || form.name.length < 3 || form.name.length > 50) {
//       toast.error('Name must be between 3 and 50 characters');
//       return;
//     }
//     if (!/^\d+(\.\d{1})?$/.test(form.number)) {
//       toast.error('Number must have at most one decimal place (e.g., 1.5)');
//       return;
//     }
//     try {
//       const payload = {
//         ...form,
//         city: normalizedCity,
//         coordinates: [parseFloat(form.latitude), parseFloat(form.longitude)],
//       };
//       const res = await saveArea(payload);
//       console.log('Save response:', res);
//       if (res.success) {
//         await loadMarkers();
//         setForm({
//           _id: '',
//           name: '',
//           color: '#ff0000',
//           number: '',
//           shape: 'circle',
//           latitude: '',
//           longitude: '',
//         });
//         setSelected(null);
//         setShowPopup(false);
//         toast.success(res.ResponseMessage);
//       } else {
//         toast.error('Failed to save area: ' + res.ResponseMessage);
//       }
//     } catch (error) {
//       console.error('Error saving area:', error);
//       toast.error('Error saving area: ' + error.message);
//     }
//   };

//   const closePopup = () => {
//     setShowPopup(false);
//     setForm({
//       _id: '',
//       name: '',
//       color: '#ff0000',
//       number: '',
//       shape: 'circle',
//       latitude: '',
//       longitude: '',
//     });
//     setEditing(false);
//     setSelected(null);
//   };

//   const handleRetry = () => {
//     setError(null);
//     setMapLoadTimeout(false);
//     setRetryCount(0);
//     setScriptLoaded(false);
//     loadMarkers();
//   };

//   return (
//     <>
//       <ToastContainer
//         position="top-center"
//         autoClose={2000}
//         hideProgressBar={false}
//         newestOnTop={false}
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         style={{ zIndex: 9999 }}
//       />
//       <div className="flex flex-col -mt-20 h-screen">
//         <h6 className="text-2xl font-bold text-center my-4">
//           Welcome to {normalizedCity.charAt(0).toUpperCase() + normalizedCity.slice(1)}'s BSI View
//         </h6>
//         <style>
//           {`
//             input[type="number"].no-arrows::-webkit-inner-spin-button,
//             input[type="number"].no-arrows::-webkit-outer-spin-button {
//               -webkit-appearance: none;
//               margin: 0;
//             }
//             input[type="number"].no-arrows {
//               -moz-appearance: textfield;
//               appearance: textfield;
//             }
//           `}
//         </style>
//         <LoadScript
//           googleMapsApiKey="AIzaSyBdwa9nNF-lhPPGNRPzCiXcngQNkSVw34M"
//           onLoad={() => {
//             console.log('Google Maps script loaded successfully');
//             setScriptLoaded(true);
//           }}
//           onError={(err) => {
//             console.error('LoadScript Error:', err);
//             setError('Failed to load Google Maps. Please check your connection or API key.');
//             setMapLoadTimeout(true);
//           }}
//         >
//           {error ? (
//             <div className="flex items-center justify-center h-full flex-col gap-4">
//               <p className="text-red-500">{error}</p>
//               <button
//                 onClick={handleRetry}
//                 className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
//               >
//                 Retry
//               </button>
//             </div>
//           ) : !scriptLoaded || !redirectAttempted ? (
//             <div className="flex items-center justify-center h-full flex-col gap-4">
//               <p>
//                 Loading Google Maps{retryCount > 0 ? ` (Attempt ${retryCount + 1}/${maxRetries + 1})` : '...'}
//               </p>
//               {mapLoadTimeout && (
//                 <button
//                   onClick={handleRetry}
//                   className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
//                 >
//                   Retry
//                 </button>
//               )}
//             </div>
//           ) : (
//             <GoogleMap
//               mapContainerStyle={{ width: '100%', height: '100%' }}
//               center={
//                 markers.length > 0
//                   ? { lat: markers[0].coordinates[0], lng: markers[0].coordinates[1] }
//                   : center
//               }
//               zoom={14}
//               onClick={mapClick}
//               options={{
//                 streetViewControl: false,
//                 fullscreenControl: true,
//                 mapTypeControl: false,
//                 mapTypeId: 'roadmap',
//                 styles: mapStyles,
//                 restriction: {
//                   latLngBounds: { north: 53.7, south: 50.5, west: 3.2, east: 7.2 },
//                   strictBounds: true,
//                 },
//               }}
//               onLoad={() => console.log('GoogleMap component loaded')}
//             >
//               {markers.map((m) => {
//                 const lat = m.coordinates[0];
//                 const lng = m.coordinates[1];
//                 let iconPath = null;
//                 let isSymbol = true;

//                 switch (m.shape) {
//                   case 'circle':
//                     iconPath = window.google.maps.SymbolPath.CIRCLE;
//                     break;
//                   case 'marker':
//                   default:
//                     isSymbol = false;
//                     break;
//                 }

//                 const markerProps = {
//                   key: m._id,
//                   position: { lat, lng },
//                   onClick: () => {
//                     setSelected(m);
//                     setEditing(true);
//                     setShowPopup(true);
//                     setForm({
//                       _id: m._id,
//                       name: m.name,
//                       color: m.color,
//                       number: m.number,
//                       shape: m.shape,
//                       latitude: lat.toString(),
//                       longitude: lng.toString(),
//                     });
//                   },
//                   label: {
//                     text: String(m.number),
//                     color: '#fff',
//                     fontWeight: 'bold',
//                     fontSize: '14px',
//                   },
//                 };

//                 if (isSymbol) {
//                   markerProps.icon = {
//                     path: iconPath,
//                     fillColor: m.color,
//                     fillOpacity: 1,
//                     strokeWeight: 1,
//                     scale: 10,
//                   };
//                 } else {
//                   markerProps.icon = {
//                     path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
//                     fillColor: m.color,
//                     fillOpacity: 1,
//                     strokeColor: '#000',
//                     strokeWeight: 1,
//                     scale: 1.5,
//                     anchor: new window.google.maps.Point(12, 24),
//                     labelOrigin: new window.google.maps.Point(12, 10),
//                   };
//                 }

//                 return <Marker {...markerProps} />;
//               })}
//             </GoogleMap>
//           )}
//         </LoadScript>

//         {showPopup && (
//           <div
//             className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 shadow-lg rounded-lg w-64"
//             style={{ zIndex: 1000 }}
//           >
//             <button
//               type="button"
//               className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none"
//               onClick={closePopup}
//               title="Close"
//             >
//               <svg
//                 className="w-6 h-6"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//                 xmlns="http://www.w3.org/2000/svg"
//               >
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
//               </svg>
//             </button>
//             <h2 className="text-lg font-bold mb-2">{editing ? 'Update Rating' : 'Submit Rating'}</h2>
//             <form onSubmit={handleFormSubmit}>
//               <label className="mb-1 flex justify-between text-sm font-medium text-slate-700">Name</label>
//               <input
//                 required
//                 minLength={3}
//                 maxLength={50}
//                 className="w-full mb-2 p-1 border rounded"
//                 value={form.name}
//                 onChange={(e) => setForm({ ...form, name: e.target.value })}
//               />
//               <label className="flex justify-between mb-1">Choose Color:</label>
//               <div className="relative">
//                 <input
//                   type="color"
//                   className="w-full mb-2 cursor-pointer"
//                   value={form.color}
//                   onChange={(e) => setForm({ ...form, color: e.target.value })}
//                 />
//               </div>
//               <label className="flex justify-between mb-1">Number:</label>
//               <input
//                 required
//                 type="number"
//                 min="0"
//                 step="0.1"
//                 className="w-full mb-2 p-1 border rounded no-arrows"
//                 value={form.number}
//                 onChange={(e) => setForm({ ...form, number: e.target.value })}
//               />
//               <label className="flex justify-between mb-1">Shape:</label>
//               <select
//                 className="w-full mb-2 p-1 border rounded"
//                 value={form.shape}
//                 onChange={(e) => setForm({ ...form, shape: e.target.value })}
//               >
//                 <option value="circle">Circle</option>
//                 <option value="marker">Marker</option>
//               </select>
//               <input
//                 required
//                 type="number"
//                 hidden
//                 step="any"
//                 className="w-full mb-2 p-1 border rounded"
//                 value={form.latitude}
//                 onChange={(e) => setForm({ ...form, latitude: e.target.value })}
//               />
//               <input
//                 required
//                 type="number"
//                 hidden
//                 step="any"
//                 className="w-full mb-2 p-1 border rounded"
//                 value={form.longitude}
//                 onChange={(e) => setForm({ ...form, longitude: e.target.value })}
//               />
//               <div className="flex justify-center mt-4 space-x-2">
//                 <button
//                   type="submit"
//                   className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
//                 >
//                   {editing ? 'Update Rating' : 'Submit Rating'}
//                 </button>
//                 {editing && (
//                   <button
//                     type="button"
//                     className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600"
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       setShowConfirmDelete(true);
//                     }}
//                   >
//                     Delete
//                   </button>
//                 )}
//                 {showConfirmDelete && (
//                   <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
//                     <div className="bg-white p-6 rounded-lg shadow-md text-center w-80">
//                       <h2 className="text-lg font-semibold mb-4">Are you sure you want to delete this rating?</h2>
//                       <div className="flex justify-around">
//                         <button
//                           className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
//                           onClick={() => setShowConfirmDelete(false)}
//                         >
//                           Cancel
//                         </button>
//                         <button
//                           type="button"
//                           className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
//                           onClick={async (e) => {
//                             try {
//                               const res = await deleteRating({ id: form._id });
//                               console.log('Delete response:', res);
//                               if (res.success) {
//                                 await loadMarkers();
//                                 toast.success('Rating deleted successfully!');
//                                 setTimeout(() => {
//                                   closePopup();
//                                 }, 500);
//                               } else {
//                                 toast.error('Delete failed: ' + res.ResponseMessage);
//                               }
//                             } catch (error) {
//                               console.error('Delete error:', error);
//                               toast.error('An error occurred while deleting the rating.');
//                             } finally {
//                               e.stopPropagation();
//                               setShowConfirmDelete(false);
//                               setEditing(false);
//                             }
//                           }}
//                         >
//                           OK
//                         </button>
//                       </div>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </form>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default HOC(SerendipityMapView);
